import {useSelector} from 'react-redux';
import gsap from 'gsap';

import {isGutenberg} from './gutenberg';
import {makeStore, buildStateSetter, buildStateGetter, observeStore} from './redux';
import {wpQuery, getAttachment, getWPJSON} from './networking';
import * as ActionCreators from './redux/actionCreators';
import * as hooks from './hooks';
import * as dom from './dom';
import * as util from './util';
import * as browser from './browser';
import * as css from './css';
import * as hoc from './hoc';
import * as icons from './icons';
import * as html from './html';
import * as colors from './colors';
import * as gtag from './gtags';
import * as networking from './networking';

const store = makeStore();

function reduxSetter(actionCreator, before = null) {
    let f = function() {
        if (before) before();
        store.dispatch(actionCreator(...arguments));
    };
    f.displayName = actionCreator.displayName ? "polyphony." + actionCreator.displayName : '';
    return f;
}

window.polyphony = {
    // Dynamically-defined fields
    // components: {backend, frontend, generic}, // set by appropriate index files for code splitting

    // Mini-Libraries in Polyphony
    browser, // Utilities for gaining information about the browser we're running in
    css, // Utilities for manipulating CSS/Style
    hoc, // Higher-Order components
    hooks, // React hooks related to Polyphony
    util, // General utilities
    dom, // DOM-related utilities
    icons, // A set of reusable icons
    html, // A React HTML parser
    colors, // Color utilities
    wpQuery, // Make a wordpress query
    getWPJSON, // Make a WordPress JSON API call
    getAttachment, // Get a wordpress attachment given an attachment id
    isGutenberg, // Predicate to determine if we're running Gutenberg
    gtag, // Google Tag Manager
    networking, // Networking operations

    // External libraries
    gsap,

    // use instead of console.log
    debug() {
        if (polyphony.getState().debug.enabled) {
            console.log(...["Polyphony:", ...arguments]); // eslint-disable-line no-console
        }
    },

    // Content Loading
    loadContent: reduxSetter(ActionCreators.loadContent, () => dom.scroll({to: 0})),
    loadMoreContent: reduxSetter(ActionCreators.loadMoreContent),

    // Body class predicates
    isAdmin: () => document.body.classList.contains('wp-admin'),
    isLoggedIn: () => document.body.classList.contains('logged-in'),

    // State Observation
    observeState: (select, onChange) => observeStore(store, select, onChange),
    useState: useSelector,
    store,
    updateState: buildStateSetter(store),
    getState: buildStateGetter(store),

    // Setters for specific state
    setHashScrollOffset: reduxSetter(ActionCreators.setHashScrollOffset),
    setLoadingIndicator: reduxSetter(ActionCreators.setLoadingIndicator),
    setContentWrapper: reduxSetter(ActionCreators.setContentWrapper),
    setPostComponent: (post_type, Post) => polyphony.setPostType(post_type, Post, null),
    setArchiveComponent: (post_type, Archive) => polyphony.setPostType(post_type, null, Archive),
    setPostType: (post_type, Post, Archive) => store.dispatch(ActionCreators.registerPostType(util.multiArg(post_type), Post, Archive)),
    setTaxonomy: (taxonomy, TaxonomyArchive) =>
        store.dispatch(ActionCreators.registerTaxonomy(util.multiArg(taxonomy), TaxonomyArchive)),
    addBlock: reduxSetter(ActionCreators.addBlock),
    registerCustomizerControl: reduxSetter(ActionCreators.registerCustomizerControl),
    setFocusMode: reduxSetter(ActionCreators.setFocusMode),
    recordWindowSize: reduxSetter(ActionCreators.setWindowSize),
    recordWindowPosition: reduxSetter(ActionCreators.setWindowPosition)
};

